import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { ArrowRightIcon } from '../components/Icons';

interface Recent {
  id: string;
  name: string;
  running: boolean;
  doneCount: number;
  shotCount: number;
}

/**
 * Three ways into a shoot, chosen first because they ask for different
 * things: a photograph you already like, the studio's own settings, or a few
 * sentences. Each page asks only what its way needs.
 *
 * The page is set like a magazine cover: the question in the largest type in
 * the app, rising a word at a time, its last word marked in ink; the three
 * ways as three cards that sit slightly askew and straighten under the
 * pointer; and, along the foot, the rolls already on the go, so picking one
 * back up is a click from here rather than a trip to the archive.
 */
export function NewShootHubPage() {
  const t = useT();
  const [recent, setRecent] = useState<Recent[]>([]);

  useEffect(() => {
    void api
      .listShoots()
      .then((list) => setRecent(list.slice(0, 6)))
      .catch(() => undefined);
  }, []);

  const ways = [
    { to: '/new/copy', key: 'copy', tone: 'mark' },
    { to: '/new/presets', key: 'presets', tone: 'paper' },
    { to: '/new/brief', key: 'brief', tone: 'ink' },
  ] as const;

  // The last word that has a letter in it takes the highlight, so the mark
  // lands on "aujourd’hui" and not on the question mark after it.
  const words = t('hub.title').split(' ');
  let marked = -1;
  words.forEach((word, index) => {
    if (/\p{L}/u.test(word)) marked = index;
  });

  return (
    <div className="app__inner hub">
      <header className="hub__head">
        <div className="hub__eyebrow">{t('hub.eyebrow')}</div>
        <h1 className="hub__title">
          {words.map((word, index) => (
            <Fragment key={index}>
              <span className="hub__mask">
                <span className="hub__word" style={{ animationDelay: `${60 + index * 70}ms` }}>
                  {index === marked ? <Marked word={word} /> : word}
                </span>
              </span>{' '}
            </Fragment>
          ))}
        </h1>
        <p className="hub__lede">{t('hub.lede')}</p>
      </header>

      <div className="ways">
        {ways.map((way, index) => (
          <Link key={way.key} to={way.to} className={`way way--${way.tone}`}>
            <span className="way__num">{String(index + 1).padStart(2, '0')}</span>
            <span className="way__title">{t(`hub.${way.key}.title`)}</span>
            <span className="way__text">{t(`hub.${way.key}.text`)}</span>
            <span className="way__go">
              {t(`hub.${way.key}.go`)}
              <ArrowRightIcon />
            </span>
          </Link>
        ))}
      </div>

      {recent.length > 0 && (
        <nav className="ticker" aria-label={t('hub.recent')}>
          {/* Twice over, so the strip can loop without a seam; the copy is
              hidden from assistive technology and from the tab order. */}
          {[0, 1].map((copy) => (
            <ul key={copy} className="ticker__run" aria-hidden={copy === 1 || undefined}>
              {recent.map((shoot) => (
                <li key={shoot.id}>
                  <Link to={`/shoots/${shoot.id}`} tabIndex={copy === 1 ? -1 : undefined}>
                    {shoot.running && <span className="ticker__live">{t('hub.live')}</span>}
                    {shoot.name}
                    <span className="ticker__count">
                      {shoot.doneCount}/{shoot.shotCount}
                    </span>
                  </Link>
                  <span className="ticker__sep" aria-hidden="true">
                    ✦
                  </span>
                </li>
              ))}
            </ul>
          ))}
        </nav>
      )}
    </div>
  );
}

/** The highlight covers the word, not the punctuation stuck to it. */
function Marked({ word }: { word: string }) {
  const [, letters = word, rest = ''] = /^(.*\p{L})(.*)$/u.exec(word) ?? [];
  return (
    <>
      <span className="hub__mark">{letters}</span>
      {rest}
    </>
  );
}
